import { useCallback, useMemo, useState } from "react";
import { RoleOption, StatusOption, OrgFilterOption } from "../../../../store/slices/GlobalSearchSlice";
import FilterDropdown from "./FilterDropdown";

interface SearchFiltersProps {
    showRoleFilter: boolean;
    showStatusFilter: boolean;
    selectedRoles: string[];
    selectedStatuses: string[];
    selectedOrgs: string[];
    roleOptions: RoleOption[];
    statusOptions: StatusOption[];
    orgFilterOptions: OrgFilterOption[];
    onRoleToggle: (role: string) => void;
    onRoleClearAll: () => void;
    onStatusToggle: (status: string) => void;
    onStatusClearAll: () => void;
    onOrgToggle: (orgId: string) => void;
    onOrgClearAll: () => void;
}

const SearchFilters = ({
    showRoleFilter,
    showStatusFilter,
    selectedRoles,
    selectedStatuses,
    selectedOrgs,
    roleOptions,
    statusOptions,
    orgFilterOptions,
    onRoleToggle,
    onRoleClearAll,
    onStatusToggle,
    onStatusClearAll,
    onOrgToggle,
    onOrgClearAll,
}: SearchFiltersProps) => {
    const [openDropdown, setOpenDropdown] = useState<string | null>(null);

    const toggleDropdown = useCallback((name: string) => {
        setOpenDropdown((prev) => (prev === name ? null : name));
    }, []);

    const roleFilterOptions = useMemo(
        () => roleOptions.map((r) => ({ value: r.role, label: r.displayName })),
        [roleOptions]
    );

    const statusFilterOptions = useMemo(
        () => statusOptions.map((s) => ({ value: s.status, label: s.displayName })),
        [statusOptions]
    );

    const orgFilterOptionsMapped = useMemo(() => {
        const allOrgs = orgFilterOptions.flatMap((org) => [
            { value: org.id, label: org.displayName },
            ...org.subOrgs.map((sub) => ({ value: sub.orgId, label: sub.displayName })),
        ]);
        return [{ value: "ALL", label: "All" }, ...allOrgs];
    }, [orgFilterOptions]);

    return (
        <div className="flex items-center gap-3">
            {showRoleFilter && (
                <FilterDropdown
                    label="Role"
                    options={roleFilterOptions}
                    selectedValues={selectedRoles}
                    onToggle={onRoleToggle}
                    onClearAll={onRoleClearAll}
                    isOpen={openDropdown === "role"}
                    onToggleOpen={() => toggleDropdown("role")}
                />
            )}

            {showStatusFilter && (
                <FilterDropdown
                    label="Status"
                    options={statusFilterOptions}
                    selectedValues={selectedStatuses}
                    onToggle={onStatusToggle}
                    onClearAll={onStatusClearAll}
                    dropdownWidth="w-56"
                    isOpen={openDropdown === "status"}
                    onToggleOpen={() => toggleDropdown("status")}
                />
            )}

            <FilterDropdown
                label="Organization"
                options={orgFilterOptionsMapped}
                selectedValues={selectedOrgs}
                onToggle={onOrgToggle}
                onClearAll={onOrgClearAll}
                dropdownWidth="w-56"
                isOpen={openDropdown === "org"}
                onToggleOpen={() => toggleDropdown("org")}
            />
        </div>
    );
};

export default SearchFilters;
